import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, Check, Loader2, Save, Star, ThumbsDown, ThumbsUp, Trash2, X,
} from 'lucide-react';
import { ADMIN_PALETTE, STATUS_PALETTE } from '../adminDesignSystem';
import { EXPERIENCE_DESCRIPTION_MAX, getExperienceCategory } from '../../../utils/experienceCategories';
import { ExperienceCategoryGlyph } from '../../host/ExperienceCategoryGlyph';
import { experiencePhotoUrl } from '../../../utils/experiencesAdminApi';

function formatSubmittedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function TierChip({ value, active, label, hint, icon, onClick }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={`admin-experience-detail-tier-${value}`}
      style={{
        flex: 1, padding: '10px 12px', borderRadius: 14,
        border: `1.5px solid ${active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
        background: active ? ADMIN_PALETTE.ink : '#FFFFFF',
        color: active ? '#FFFFFF' : ADMIN_PALETTE.ink,
        cursor: 'pointer', textAlign: 'left',
        fontFamily: "'Manrope', sans-serif",
        display: 'flex', flexDirection: 'column', gap: 3,
        transition: 'background 140ms ease, color 140ms ease',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 800 }}>
        {icon}
        {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>{hint}</span>
    </button>
  );
}

function ActionButton({ children, onClick, disabled, testId, variant }) {
  let bg = ADMIN_PALETTE.ink;
  let color = '#FFFFFF';
  let border = ADMIN_PALETTE.ink;
  if (variant === 'approve') {
    bg = STATUS_PALETTE.active.solid; border = STATUS_PALETTE.active.solid;
  } else if (variant === 'decline') {
    bg = '#FFFFFF'; color = STATUS_PALETTE.urgent.text;
    border = STATUS_PALETTE.urgent.solid;
  } else if (variant === 'danger') {
    bg = STATUS_PALETTE.urgent.soft; color = STATUS_PALETTE.urgent.text;
    border = STATUS_PALETTE.urgent.solid + '55';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '11px 14px', borderRadius: 12,
        border: `1.5px solid ${border}`, background: bg, color,
        fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 800,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'transform 120ms ease, opacity 120ms ease',
        minHeight: 44,
      }}
    >
      {children}
    </button>
  );
}

export function ExperienceDetailCard({
  experience,
  onClose,
  onApprove,
  onDecline,
  onSave,
  onDelete,
  isMobileView,
  saving,
}) {
  const initialTier = experience?.tier || 'regular';
  const initialDesc = experience?.description || '';
  const [tier, setTier] = useState(initialTier);
  const [description, setDescription] = useState(initialDesc);

  useEffect(() => {
    setTier(experience?.tier || 'regular');
    setDescription(experience?.description || '');
  }, [experience?.id, experience?.tier, experience?.description]);

  if (!experience) return null;
  const cat = getExperienceCategory(experience.category) || null;
  const isPending = experience.status === 'pending';
  const dirty = description !== initialDesc || tier !== initialTier;

  const shellStyle = isMobileView ? {
    position: 'fixed',
    left: 0, right: 0, bottom: 0, zIndex: 9500,
    background: '#FFFFFF',
    borderRadius: '20px 20px 0 0',
    boxShadow: '0 -20px 60px rgba(15,17,21,0.28)',
    padding: '10px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
    maxHeight: '92dvh',
    overflowY: 'auto',
    animation: 'adminExperiencesSheetIn 220ms ease',
  } : {
    position: 'sticky',
    top: 12,
    background: '#FFFFFF',
    border: `1px solid ${ADMIN_PALETTE.border}`,
    borderRadius: 20,
    padding: 18,
    boxShadow: '0 20px 40px rgba(15,17,21,0.10)',
    width: 380,
    maxHeight: 'calc(100dvh - 40px)',
    overflowY: 'auto',
    flexShrink: 0,
  };

  return (
    <div
      data-testid="admin-experience-detail"
      style={shellStyle}
    >
      {isMobileView && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 10px' }}>
          <div style={{ width: 44, height: 4, borderRadius: 9999, background: '#E5E7EB' }} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: cat?.color || '#7C3AED',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <ExperienceCategoryGlyph glyph={cat?.glyph || 'target'} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontSize: 15, fontWeight: 800, color: ADMIN_PALETTE.ink,
            fontFamily: "'Manrope', sans-serif", lineHeight: 1.2,
          }}>
            {cat?.display || 'Experience'}
          </p>
          <p style={{
            margin: '2px 0 0', fontSize: 12, fontWeight: 600, color: ADMIN_PALETTE.inkMuted,
            fontFamily: "'Manrope', sans-serif",
          }}>
            by {experience.host_name || 'Host'} · {formatSubmittedAt(experience.created_at)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="admin-experience-detail-close"
          aria-label="Close"
          style={{
            background: '#F3F4F6', border: 'none', borderRadius: 9999,
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} color={ADMIN_PALETTE.inkSoft} />
        </button>
      </div>

      {experience.photo_url ? (
        <div style={{
          borderRadius: 14, overflow: 'hidden',
          border: `1px solid ${ADMIN_PALETTE.border}`,
          marginBottom: 12, background: '#0F1B2A',
        }}>
          <img
            src={experiencePhotoUrl(experience.photo_url)}
            alt="Experience"
            data-testid="admin-experience-detail-photo"
            style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      ) : null}

      <div
        data-testid="admin-experience-detail-reactions"
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          gap: 8, marginBottom: 12,
        }}
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 9999,
          background: '#ECFDF5', color: '#047857',
          fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 800,
          border: '1px solid #A7F3D0',
        }}>
          <ThumbsUp size={12} strokeWidth={2.4} />
          {Math.max((experience.like_count || 0) - (experience.dislike_count || 0), 0)} likes
        </span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 9999,
          background: '#FEF2F2', color: '#B91C1C',
          fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 800,
          border: '1px solid #FECACA',
        }}>
          <ThumbsDown size={12} strokeWidth={2.4} />
          {experience.dislike_count || 0} dislikes
        </span>
        {experience.flagged ? (
          <span
            data-testid="admin-experience-detail-flagged"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 9999,
              background: '#FEE2E2', color: '#B91C1C',
              fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 800,
              border: '1px solid #EF4444',
            }}
          >
            <AlertTriangle size={12} strokeWidth={2.4} />
            Flagged · high dislikes
          </span>
        ) : null}
      </div>

      {Array.isArray(experience.tags) && experience.tags.length ? (
        <div data-testid="admin-experience-detail-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {experience.tags.map((tag) => (
            <span key={tag} style={{
              padding: '3px 9px', borderRadius: 9999,
              background: `${cat?.color || '#7C3AED'}18`, color: cat?.color || '#7C3AED',
              fontSize: 11, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
              border: `1px solid ${cat?.color || '#7C3AED'}40`,
            }}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      <p style={{
        margin: '0 0 12px', fontSize: 11.5, color: ADMIN_PALETTE.inkMuted,
        fontFamily: "'Manrope', sans-serif", letterSpacing: 0.2,
      }}>
        {experience.latitude?.toFixed(5)}, {experience.longitude?.toFixed(5)}
      </p>

      <label style={{
        display: 'block', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted,
        fontFamily: "'Manrope', sans-serif", marginBottom: 6,
      }}>
        Description · guests see this
      </label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value.slice(0, EXPERIENCE_DESCRIPTION_MAX))}
        data-testid="admin-experience-detail-description"
        rows={3}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: 10, borderRadius: 12,
          border: `1.5px solid ${ADMIN_PALETTE.border}`,
          fontFamily: "'Manrope', sans-serif", fontSize: 13.5,
          color: ADMIN_PALETTE.ink, resize: 'vertical',
          background: '#FDFDFD',
        }}
      />
      <p style={{
        margin: '4px 0 14px', textAlign: 'right', fontSize: 11,
        color: ADMIN_PALETTE.inkMuted, fontFamily: "'Manrope', sans-serif",
      }}>
        {description.length}/{EXPERIENCE_DESCRIPTION_MAX}
      </p>

      <label style={{
        display: 'block', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted,
        fontFamily: "'Manrope', sans-serif", marginBottom: 6,
      }}>
        Tier · pick before approving
      </label>
      <div
        data-testid="admin-experience-detail-tier"
        role="radiogroup"
        aria-label="Experience tier"
        style={{ display: 'flex', gap: 8, marginBottom: 16 }}
      >
        <TierChip
          value="regular"
          active={tier === 'regular'}
          label="Regular"
          hint="Free for guests"
          onClick={() => setTier('regular')}
        />
        <TierChip
          value="premium"
          active={tier === 'premium'}
          label="Premium"
          hint="Locked · package"
          onClick={() => setTier('premium')}
          icon={<Star size={12} fill="#F59E0B" color="#F59E0B" />}
        />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isPending ? '1fr 1fr' : '1fr 1fr',
        gap: 8, marginBottom: 8,
      }}>
        {isPending ? (
          <>
            <ActionButton
              testId="admin-experience-detail-approve"
              onClick={() => onApprove({ tier, description })}
              disabled={saving}
              variant="approve"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Approve
            </ActionButton>
            <ActionButton
              testId="admin-experience-detail-decline"
              onClick={onDecline}
              disabled={saving}
              variant="decline"
            >
              <X size={16} />
              Decline
            </ActionButton>
          </>
        ) : (
          <>
            <ActionButton
              testId="admin-experience-detail-save"
              onClick={() => onSave({ description, tier })}
              disabled={saving || !dirty}
              variant="approve"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save changes
            </ActionButton>
            <ActionButton
              testId="admin-experience-detail-decline"
              onClick={onDecline}
              disabled={saving}
              variant="decline"
            >
              <X size={16} />
              Un-approve
            </ActionButton>
          </>
        )}
      </div>
      <ActionButton
        testId="admin-experience-detail-delete"
        onClick={onDelete}
        disabled={saving}
        variant="danger"
      >
        <Trash2 size={16} />
        Delete permanently
      </ActionButton>
    </div>
  );
}
